// Shared insight engine + label config, used by both the Home status board and
// the Admin insights editor so the auto-conclusions never drift between them.

export const COUNTRY_NAMES = { brazil: 'Brazil', mexico: 'Mexico', chile: 'Chile', argentina: 'Argentina', uruguay: 'Uruguay', colombia: 'Colombia' };
const CANON = ['brazil', 'mexico', 'chile', 'argentina', 'uruguay', 'colombia'];

// Fixed label set (no red — "Needs attention" is orange per request).
export const INSIGHT_LABELS = [
  { id: 'bau', name: 'Business as usual', dot: 'bg-emerald-500', pill: 'bg-emerald-50 text-emerald-700 border-emerald-200', ring: 'ring-emerald-500 bg-emerald-500' },
  { id: 'watch', name: 'Watch', dot: 'bg-amber-500', pill: 'bg-amber-50 text-amber-700 border-amber-200', ring: 'ring-amber-500 bg-amber-500' },
  { id: 'attention', name: 'Needs attention', dot: 'bg-orange-500', pill: 'bg-orange-50 text-orange-700 border-orange-200', ring: 'ring-orange-500 bg-orange-500' },
];
export const labelById = (id) => INSIGHT_LABELS.find(l => l.id === id) || INSIGHT_LABELS[0];

// Per-country auto conclusion crossing plan achievement (ACT+FC vs AOP) and
// sell-out trend (QoQ). Inventory is intentionally excluded for now.
export function computeCountryInsights(forecastData = {}, sellOutData = {}) {
  const rows = Object.keys(forecastData || {}).map(key => {
    const fc = forecastData[key];
    let sales = 0, aop = 0;
    const sd = fc?.actualSales;
    (Array.isArray(sd) ? sd : Object.values(sd || {})).forEach(v => { if (typeof v === 'number') sales += v; });
    if (Array.isArray(fc?.aopMonthly)) fc.aopMonthly.forEach(v => { if (typeof v === 'number') aop += v; });
    else if (fc?.aopMonthly && typeof fc.aopMonthly === 'object') Object.values(fc.aopMonthly).forEach(v => { if (typeof v === 'number') aop += v; });
    else if (typeof fc?.aop === 'number') aop += fc.aop;
    const aopPct = aop > 0 ? Math.round((sales / aop) * 100) : null;

    const so = sellOutData?.[key];
    let soChange = null;
    if (so?.skus && so?.months) {
      const skus = so.skus.filter(s => !s.isGroup);
      const mt = so.months.map((_, i) => skus.reduce((s, sku) => s + ((sku.values || [])[i] || 0), 0));
      const qCur = mt.slice(-3).reduce((s, v) => s + v, 0);
      const qPrev = mt.slice(-6, -3).reduce((s, v) => s + v, 0);
      soChange = qPrev > 0 ? Math.round(((qCur - qPrev) / qPrev) * 100) : null;
    }

    let score = 0;
    const planGap = (aopPct != null && aopPct < 100) ? 100 - aopPct : null;
    const selloutDrop = (soChange != null && soChange < 0) ? soChange : null;
    if (planGap != null) score += aopPct < 75 ? 3 : aopPct < 90 ? 2 : 1;
    if (selloutDrop != null) score += selloutDrop <= -10 ? 2 : 1;

    let headline = '', detail = '';
    if (planGap != null && selloutDrop != null) {
      headline = 'Below plan with sell-out cooling';
      detail = `Running ${planGap}pp under AOP and sell-through down ${Math.abs(selloutDrop)}% — demand needs a push.`;
    } else if (planGap != null) {
      headline = planGap >= 25 ? 'Sales well below plan' : `Trailing plan by ${planGap}pp`;
      detail = 'Sales under AOP — review country contribution and phasing for the rest of the year.';
    } else if (selloutDrop != null) {
      headline = `Sell-out down ${Math.abs(selloutDrop)}% QoQ`;
      detail = 'Partner sell-through is slowing — watch in-market pull before the next order.';
    } else {
      headline = 'On plan';
      detail = 'Sales tracking to plan with stable sell-out.';
    }
    const autoLabel = score >= 3 ? 'attention' : score > 0 ? 'watch' : 'bau';
    return { key, name: COUNTRY_NAMES[key] || key, sales, aop, aopPct, soChange, score, autoHeadline: headline, autoDetail: detail, autoLabel };
  }).filter(r => r.sales || r.aop);

  const idx = (k) => { const i = CANON.indexOf(k); return i < 0 ? 99 : i; };
  return rows.sort((a, b) => idx(a.key) - idx(b.key) || a.name.localeCompare(b.name));
}
